
import { useEffect, useMemo, useRef, useState } from 'react'
import { Icon } from '@/components/ui/Icon'
import { CATEGORY_META, DEVICE_CATEGORIES, categoryFromName, type DeviceCategory } from '@/lib/categories'
import {
  CONNECTORS_BY_SIGNAL,
  PORT_DIRECTIONS,
  SIGNAL_CATEGORIES,
  SIGNAL_META,
  DIRECTION_LABELS,
  type PortDirection,
  type SignalCategory,
} from '@/lib/signals'
import { btn, cx, iconBtn, input, label, notice, segment } from '@/lib/ui'
import { plural, verificationSummary } from '@/lib/format'
import { useUiMode } from '@/components/workspace/UiModeProvider'
import type { DeviceDTO } from '@/lib/types'
import { SlotsEditor, type EditableSlot } from './SlotsEditor'
import { errorMessage } from '@/lib/client-api'

type EditablePort = {
  key: string
  id?: string
  name: string
  direction: PortDirection
  connector: string
  signal: SignalCategory
}

const newKey = () =>
  typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : Math.random().toString(36).slice(2)

/** Only onboard ports are edited here; card copies belong to their slot. */
function toEditable(device: DeviceDTO | null): EditablePort[] {
  if (!device) return []
  return device.ports
    .filter((port) => !port.slotId)
    .map((port) => ({
      key: port.id,
      id: port.id,
      name: port.name,
      direction: port.direction,
      connector: port.connector,
      signal: port.signal,
    }))
}

function toEditableSlots(device: DeviceDTO | null): EditableSlot[] {
  if (!device) return []
  return device.slots.map((slot) => ({
    key: slot.id,
    id: slot.id,
    label: slot.label,
    bus: slot.bus,
    cardId: slot.cardId,
  }))
}

const SPEC_FIELDS = [
  { key: 'cpu', label: 'CPU', placeholder: 'AMD Threadripper 7970X' },
  { key: 'ram', label: 'RAM', placeholder: '128 GB' },
  { key: 'gpu', label: 'GPU', placeholder: 'RTX 4090' },
  { key: 'storage', label: 'Storage', placeholder: '2 × 4 TB NVMe' },
  { key: 'os', label: 'OS', placeholder: 'Windows 11 Pro' },
] as const

type SpecKey = (typeof SPEC_FIELDS)[number]['key']

export function DeviceEditor({
  device,
  createUrl,
  deviceUrl,
  cards,
  onSaved,
  onCancel,
}: {
  device: DeviceDTO | null
  /** POST target for a new device. */
  createUrl: string
  /**
   * The resource URL of an existing device (receives the device id appended).
   * PUT saves it; GET is what counts the cable runs on each port when the
   * editor opens, so the endpoint has to answer both.
   */
  deviceUrl: (id: string) => string
  /** Capture cards a computer defined here may install. */
  cards: DeviceDTO[]
  onSaved: (device: DeviceDTO) => void
  onCancel: () => void
}) {
  const { advanced } = useUiMode()
  const [name, setName] = useState(device?.name ?? '')
  const [manufacturer, setManufacturer] = useState(device?.manufacturer ?? '')
  const [model, setModel] = useState(device?.model ?? '')
  const [formFactorRU, setFormFactorRU] = useState(String(device?.formFactorRU ?? 1))
  const [ipAddress, setIpAddress] = useState(device?.ipAddress ?? '')
  const [notes, setNotes] = useState(device?.notes ?? '')
  const [sourceUrl, setSourceUrl] = useState(device?.sourceUrl ?? '')
  const [verified, setVerified] = useState(device?.verified ?? false)
  // null = follow the type label until the user picks one explicitly.
  const [category, setCategory] = useState<DeviceCategory | null>(device?.category ?? null)
  const [specs, setSpecs] = useState<Record<SpecKey, string>>({
    cpu: device?.cpu ?? '',
    ram: device?.ram ?? '',
    gpu: device?.gpu ?? '',
    storage: device?.storage ?? '',
    os: device?.os ?? '',
  })
  const [slots, setSlots] = useState<EditableSlot[]>(() => toEditableSlots(device))
  const [ports, setPorts] = useState<EditablePort[]>(() => toEditable(device))
  const [error, setError] = useState<string | null>(null)
  /**
   * Cable runs per port id, fetched once when the editor opens.
   *
   * `ProjectEdge` cascades from `Port`, so removing a connector silently takes
   * somebody's cable runs with it. The API refuses — but only on save, by which
   * point the user has done the work and the message arrives as a wall. The
   * count belongs at the click.
   *
   * null = not known (still loading, or the request failed). Absence is not
   * zero: the editor stays out of the way and lets the save-time refusal do its
   * job, which is the enforcement either way.
   */
  const [runsByPortId, setRunsByPortId] = useState<Record<string, number> | null>(null)
  /** Port key whose removal has been questioned but not yet answered. */
  const [pendingRemoval, setPendingRemoval] = useState<string | null>(null)
  /**
   * The user has already said "Remove anyway" to a port with runs on it, so the
   * save carries that answer and the server does not ask the same question
   * again through a native confirm().
   */
  const [confirmedRunLoss, setConfirmedRunLoss] = useState(false)
  const [saving, setSaving] = useState(false)

  const effectiveCategory = category ?? categoryFromName(name)
  const computer = effectiveCategory === 'COMPUTER'

  // Draft row for adding ports, with a count so a 16-input router doesn't
  // take sixteen clicks.
  const [draftName, setDraftName] = useState('SDI In')
  const [draftDirection, setDraftDirection] = useState<PortDirection>('INPUT')
  const [draftSignal, setDraftSignal] = useState<SignalCategory>('VIDEO')
  const [draftConnector, setDraftConnector] = useState(CONNECTORS_BY_SIGNAL.VIDEO[0])
  const [draftCount, setDraftCount] = useState('1')

  const grouped = useMemo(
    () => ({
      INPUT: ports.filter((port) => port.direction === 'INPUT'),
      OUTPUT: ports.filter((port) => port.direction === 'OUTPUT'),
    }),
    [ports],
  )

  function becomeDevice() {
    if (
      slots.length > 0 &&
      !confirm(
        `Switching to a plain device removes its ${plural(slots.length, 'slot')} and any cables patched to their ports. Continue?`,
      )
    ) {
      return
    }
    setSlots([])
    // Pick a concrete category: falling back to the name guess could land on
    // COMPUTER again ("Workstation") and leave the toggle looking broken.
    const restored = device && device.category !== 'COMPUTER' ? device.category : categoryFromName(name)
    setCategory(restored === 'COMPUTER' ? 'OTHER' : restored)
  }

  function addPorts() {
    const count = Math.min(Math.max(Number(draftCount) || 1, 1), 64)
    const base = draftName.trim() || 'Port'
    const additions: EditablePort[] = Array.from({ length: count }, (_, index) => ({
      key: newKey(),
      name: count === 1 ? base : `${base} ${index + 1}`,
      direction: draftDirection,
      connector: draftConnector,
      signal: draftSignal,
    }))
    setPorts((current) => [...current, ...additions])
  }

  /** A catalogue row's canvases belong to other workspaces; see the refusal below. */
  const isCatalogue = device?.workspaceId === null
  const deviceId = device?.id ?? null
  // `deviceUrl` is rebuilt by the parent on every one of ITS renders, so making
  // it a dependency would refetch the counts on every keystroke in the library
  // search box. The device is what the request depends on.
  const deviceUrlRef = useRef(deviceUrl)
  useEffect(() => {
    deviceUrlRef.current = deviceUrl
  }, [deviceUrl])
  useEffect(() => {
    if (!deviceId) return
    const abort = new AbortController()
    fetch(deviceUrlRef.current(deviceId), { signal: abort.signal })
      .then((response) => (response.ok ? response.json() : null))
      .then((loaded: DeviceDTO | null) => {
        if (!loaded) return
        const counts: Record<string, number> = {}
        for (const port of loaded.ports) {
          if (port.cableRuns !== undefined) counts[port.id] = port.cableRuns
        }
        setRunsByPortId(counts)
      })
      // A failed count is not an error worth showing: the save still refuses.
      .catch(() => {})
    return () => abort.abort()
  }, [deviceId])

  /** Runs drawn on a port, or undefined when nobody knows yet. */
  function runsOn(port: EditablePort): number | undefined {
    return port.id ? runsByPortId?.[port.id] : undefined
  }

  function updatePort(key: string, patch: Partial<EditablePort>) {
    setPorts((current) =>
      current.map((port) => {
        if (port.key !== key) return port
        const next = { ...port, ...patch }
        // Changing signal category re-points the connector at that category's
        // catalogue rather than leaving an XLR on a power port.
        if (patch.signal && !CONNECTORS_BY_SIGNAL[patch.signal].includes(next.connector)) {
          next.connector = CONNECTORS_BY_SIGNAL[patch.signal][0]
        }
        return next
      }),
    )
  }

  function dropPort(key: string) {
    const port = ports.find((candidate) => candidate.key === key)
    // Dropping a port with runs on it is the answer to the notice below; the
    // save sends it along rather than letting the server ask again.
    if (port && runsOn(port)) setConfirmedRunLoss(true)
    setPendingRemoval(null)
    setPorts((current) => current.filter((port) => port.key !== key))
  }

  function removePort(key: string) {
    const port = ports.find((candidate) => candidate.key === key)
    // Nothing drawn on it, or nothing known about it — remove as before.
    if (!port || !runsOn(port)) return dropPort(key)
    setPendingRemoval(key)
  }

  async function save(event: React.FormEvent) {
    event.preventDefault()
    setError(null)
    setSaving(true)

    const body: Record<string, unknown> = {
      name,
      manufacturer,
      model,
      category: effectiveCategory,
      cpu: specs.cpu,
      ram: specs.ram,
      gpu: specs.gpu,
      storage: specs.storage,
      os: specs.os,
      formFactorRU: Number(formFactorRU) || 0,
      ipAddress,
      notes,
      sourceUrl,
      verified,
      ports: ports.map((port, index) => ({
        id: port.id,
        name: port.name,
        direction: port.direction,
        connector: port.connector,
        signal: port.signal,
        position: index,
      })),
      slots: computer
        ? slots.map((slot) => ({ id: slot.id, label: slot.label, bus: slot.bus, cardId: slot.cardId }))
        : [],
    }

    const send = async (confirmRunLoss: boolean) => {
      const response = await fetch(device ? deviceUrl(device.id) : createUrl, {
        method: device ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...body, confirmRunLoss }),
      })
      return { response, payload: await response.json() }
    }

    try {
      let { response, payload } = await send(confirmedRunLoss)

      // Removing a port deletes the cable runs drawn on it. The server counts
      // them and refuses once, so the choice is made with the real number in
      // view rather than discovered on someone's run sheet later. Normally the
      // question was already answered at the click and `confirmedRunLoss` went
      // with the request; this only fires when the count never loaded, so the
      // editor had nothing to warn with. A catalogue device never offers this —
      // those runs are in workspaces this editor cannot see, and that refusal
      // carries no `confirm`.
      if (response.status === 409 && payload?.details?.confirm === 'confirmRunLoss') {
        if (!confirm(`${payload.error} Continue?`)) {
          setSaving(false)
          return
        }
        ;({ response, payload } = await send(true))
      }

      if (!response.ok) {
        // Validation errors carry per-field messages ("Two slots are labelled
        // "Slot 3"") that are far more useful than the generic headline.
        const issues: string[] = Array.isArray(payload.details)
          ? payload.details.map((issue: { message?: string }) => issue.message).filter(Boolean)
          : []
        throw new Error(
          issues.length > 0
            ? issues.join(' ')
            : payload.error === 'That record already exists'
              ? 'A device with this manufacturer, model and name already exists here.'
              : (payload.error ?? 'Could not save device'),
        )
      }
      onSaved(payload as DeviceDTO)
    } catch (cause) {
      setError(errorMessage(cause, 'Could not save device'))
    } finally {
      setSaving(false)
    }
  }

  const installedIn = device?.installedInCount ?? 0

  return (
    <form onSubmit={save} className="flex min-h-0 flex-1 flex-col">
      <div className="min-h-0 flex-1 space-y-6 overflow-y-auto p-5 sm:p-6">
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="sm:col-span-2">
            <p className={label}>Kind</p>
            <div role="group" aria-label="Kind" className="mt-1.5 inline-flex gap-0.5 rounded-full bg-hover p-0.5">
              <button
                type="button"
                aria-pressed={!computer}
                onClick={becomeDevice}
                className={cx(segment.base, !computer ? segment.on : segment.off)}
              >
                Device
              </button>
              <button
                type="button"
                aria-pressed={computer}
                onClick={() => setCategory('COMPUTER')}
                className={cx(segment.base, computer ? segment.on : segment.off)}
              >
                Computer
              </button>
            </div>
            <p className="mt-1 text-[11px] text-muted">
              A computer has slots that hold capture cards; its cards&apos; ports appear on the canvas with its own.
            </p>
          </div>
          <div>
            <label className={label} htmlFor="device-category">
              Category
            </label>
            <select
              id="device-category"
              className={cx(input, 'mt-1.5')}
              value={effectiveCategory}
              disabled={computer}
              onChange={(event) => setCategory(event.target.value as DeviceCategory)}
            >
              {DEVICE_CATEGORIES.map((value) => (
                <option key={value} value={value}>
                  {CATEGORY_META[value].singular}
                </option>
              ))}
            </select>
            {category === null && !computer && (
              <p className="mt-1 text-[11px] text-muted">Guessed from the type; change it if it is wrong.</p>
            )}
          </div>
          <div>
            <label className={label} htmlFor="device-name">
              Type
            </label>
            <input
              id="device-name"
              className={cx(input, 'mt-1.5')}
              value={name}
              onChange={(event) => setName(event.target.value)}
              placeholder="Production Switcher"
              required
            />
            <p className="mt-1 text-[11px] text-muted">
              Free-text subtitle, e.g. Production Switcher. The category groups and filters it.
            </p>
          </div>
          <div>
            <label className={label} htmlFor="device-manufacturer">
              Manufacturer
            </label>
            <input
              id="device-manufacturer"
              className={cx(input, 'mt-1.5')}
              value={manufacturer}
              onChange={(event) => setManufacturer(event.target.value)}
              placeholder="Blackmagic Design"
              required
            />
          </div>
          <div>
            <label className={label} htmlFor="device-model">
              Model
            </label>
            <input
              id="device-model"
              className={cx(input, 'mt-1.5')}
              value={model}
              onChange={(event) => setModel(event.target.value)}
              placeholder="ATEM 4 M/E"
              required
            />
          </div>
          {advanced && (
          <div>
            <label className={label} htmlFor="device-ru">
              Form factor (RU)
            </label>
            <input
              id="device-ru"
              type="number"
              min={0}
              max={60}
              className={cx(input, 'mt-1.5')}
              value={formFactorRU}
              onChange={(event) => setFormFactorRU(event.target.value)}
            />
            <p className="mt-1 text-[11px] text-muted">0 for non-rack gear.</p>
          </div>
          )}
          {advanced && (
          <div>
            <label className={label} htmlFor="device-ip">
              IP address <span className="normal-case tracking-normal text-muted/70">(optional)</span>
            </label>
            <input
              id="device-ip"
              className={cx(input, 'mt-1.5')}
              value={ipAddress}
              onChange={(event) => setIpAddress(event.target.value)}
              placeholder="192.168.10.40"
            />
          </div>
          )}
          <div className="sm:col-span-2">
            <label className={label} htmlFor="device-notes">
              Notes <span className="normal-case tracking-normal text-muted/70">(optional)</span>
            </label>
            <textarea
              id="device-notes"
              rows={2}
              className={cx(input, 'mt-1.5 resize-y')}
              value={notes}
              onChange={(event) => setNotes(event.target.value)}
              placeholder="Redundant PSU. Requires tri-level sync."
            />
          </div>
          {/* Provenance and verification exist so a shared catalogue can be
              trusted. Someone adding their own mixer does not need either. */}
          {advanced && (
          <div className="sm:col-span-2">
            <label className={label} htmlFor="device-source">
              Spec source <span className="normal-case tracking-normal text-muted/70">(optional)</span>
            </label>
            <input
              id="device-source"
              className={cx(input, 'mt-1.5')}
              value={sourceUrl}
              onChange={(event) => setSourceUrl(event.target.value)}
              placeholder="https://manufacturer.com/product/techspecs"
            />
            <label className="mt-2 flex items-center gap-2 text-xs text-muted">
              <input
                type="checkbox"
                checked={verified}
                onChange={(event) => setVerified(event.target.checked)}
                className="h-3.5 w-3.5 accent-[var(--ink)]"
              />
              Ports checked against the source spec sheet
            </label>
            {device?.verified && <p className="mt-1 text-[11px] text-muted">{verificationSummary(device)}</p>}
            {verified && !device?.verified && (
              <p className="mt-1 text-[11px] text-muted">Will be recorded as verified by you when saved.</p>
            )}
          </div>
          )}
        </div>

        {computer && (
          <section>
            <h3 className="text-base font-semibold tracking-tight">Computer</h3>
            <div className="mt-3 rounded-2xl border border-[var(--border)] bg-[var(--surface-raised)] p-4">
              <div className="grid gap-3 sm:grid-cols-2">
                {SPEC_FIELDS.map((field) => (
                  <div key={field.key}>
                    <label className={label} htmlFor={`device-${field.key}`}>
                      {field.label}
                    </label>
                    <input
                      id={`device-${field.key}`}
                      className={cx(input, 'mt-1.5')}
                      value={specs[field.key]}
                      onChange={(event) => setSpecs((current) => ({ ...current, [field.key]: event.target.value }))}
                      placeholder={field.placeholder}
                      maxLength={120}
                    />
                  </div>
                ))}
              </div>
            </div>
          </section>
        )}

        {computer && (
          <SlotsEditor
            slots={slots}
            cards={cards}
            existingSlots={device?.slots ?? []}
            onChange={setSlots}
          />
        )}

        <section>
          <div className="flex items-baseline justify-between">
            <h3 className="text-base font-semibold tracking-tight">{computer ? 'Onboard ports' : 'Ports'}</h3>
            <p className="font-mono text-[11px] text-muted">
              {grouped.INPUT.length} in · {grouped.OUTPUT.length} out
            </p>
          </div>
          {computer && (
            <p className="mt-1 text-[11px] text-muted">
              Ports on the chassis itself — network, power, onboard video. Card ports come from the slots above.
            </p>
          )}
          {installedIn > 0 && (
            <p className={cx('mt-3 text-xs', notice.warning)}>
              This card is installed in {installedIn} computer{installedIn === 1 ? '' : 's'}. Port changes
              apply to every one of them; removed or renamed ports drop their cables.
            </p>
          )}

          <div className="mt-3 rounded-2xl border border-[var(--border)] bg-[var(--surface-raised)] p-4">
            <p className={label}>Add ports</p>
            <div className="mt-2.5 grid gap-2 sm:grid-cols-[1fr_auto_auto_1fr_auto_auto]">
              <input
                className={input}
                value={draftName}
                onChange={(event) => setDraftName(event.target.value)}
                placeholder="Port name"
                aria-label="New port name"
              />
              <select
                className={input}
                value={draftDirection}
                onChange={(event) => setDraftDirection(event.target.value as PortDirection)}
                aria-label="New port direction"
              >
                {PORT_DIRECTIONS.map((direction) => (
                  <option key={direction} value={direction}>
                    {DIRECTION_LABELS[direction]}
                  </option>
                ))}
              </select>
              <select
                className={input}
                value={draftSignal}
                onChange={(event) => {
                  const signal = event.target.value as SignalCategory
                  setDraftSignal(signal)
                  setDraftConnector(CONNECTORS_BY_SIGNAL[signal][0])
                }}
                aria-label="New port signal category"
              >
                {SIGNAL_CATEGORIES.map((signal) => (
                  <option key={signal} value={signal}>
                    {SIGNAL_META[signal].label}
                  </option>
                ))}
              </select>
              <input
                className={input}
                list="connector-options"
                value={draftConnector}
                onChange={(event) => setDraftConnector(event.target.value)}
                placeholder="Connector"
                aria-label="New port connector"
              />
              <input
                type="number"
                min={1}
                max={64}
                className={cx(input, 'w-20')}
                value={draftCount}
                onChange={(event) => setDraftCount(event.target.value)}
                aria-label="How many ports to add"
                title="How many to add — names are numbered automatically"
              />
              <button type="button" onClick={addPorts} className={cx(btn.base, btn.secondary)}>
                Add
              </button>
            </div>
            <datalist id="connector-options">
              {CONNECTORS_BY_SIGNAL[draftSignal].map((connector) => (
                <option key={connector} value={connector} />
              ))}
            </datalist>
          </div>

          {ports.length === 0 ? (
            <p className="mt-3 rounded-2xl border border-dashed border-[var(--border-strong)] px-3 py-8 text-center text-xs text-muted">
              {computer
                ? 'No onboard ports yet. Most computers have at least a network port and an AC inlet.'
                : 'No ports yet. A device needs ports before it can be cabled on a canvas.'}
            </p>
          ) : (
            <ul className="mt-3 space-y-1.5">
              {ports.map((port) => (
                <li
                  key={port.key}
                  className="rounded-xl border border-[var(--border)] bg-surface p-2 shadow-sm"
                >
                 <div className="grid items-center gap-2 sm:grid-cols-[1fr_auto_auto_1fr_auto]">
                  <input
                    className={input}
                    value={port.name}
                    onChange={(event) => updatePort(port.key, { name: event.target.value })}
                    aria-label="Port name"
                  />
                  <select
                    className={input}
                    value={port.direction}
                    onChange={(event) =>
                      updatePort(port.key, { direction: event.target.value as PortDirection })
                    }
                    aria-label="Port direction"
                  >
                    {PORT_DIRECTIONS.map((direction) => (
                      <option key={direction} value={direction}>
                        {DIRECTION_LABELS[direction]}
                      </option>
                    ))}
                  </select>
                  <select
                    className={input}
                    value={port.signal}
                    onChange={(event) =>
                      updatePort(port.key, { signal: event.target.value as SignalCategory })
                    }
                    aria-label="Signal category"
                    style={{ color: SIGNAL_META[port.signal].text }}
                  >
                    {SIGNAL_CATEGORIES.map((signal) => (
                      <option key={signal} value={signal}>
                        {SIGNAL_META[signal].label}
                      </option>
                    ))}
                  </select>
                  <input
                    className={input}
                    list={`connectors-${port.signal}`}
                    value={port.connector}
                    onChange={(event) => updatePort(port.key, { connector: event.target.value })}
                    aria-label="Connector type"
                  />
                  <button
                    type="button"
                    onClick={() => removePort(port.key)}
                    aria-label={
                      runsOn(port)
                        ? `Remove ${port.name} — ${plural(runsOn(port) as number, 'cable run')} drawn on it`
                        : `Remove ${port.name}`
                    }
                    className={cx(iconBtn, 'hover:bg-danger-soft hover:text-danger')}
                  >
                    <Icon name="close" className="h-4 w-4" />
                  </button>
                 </div>

                  {/* The consequence, at the click rather than on save. Shown in
                      Simple mode too: a control can be hidden, a loss cannot. */}
                  {pendingRemoval === port.key && (
                    <div className={cx(notice.warning, 'mt-2 flex flex-wrap items-center gap-x-3 gap-y-2')}>
                      {isCatalogue ? (
                        <span className="flex-1">
                          {plural(runsOn(port) as number, 'cable run')} already drawn on <strong>{port.name}</strong> in
                          other workspaces. You cannot see those canvases, so ROUTR will not remove it here —
                          rename the port instead, which keeps them.
                        </span>
                      ) : (
                        <span className="flex-1">
                          Removing <strong>{port.name}</strong> deletes {plural(runsOn(port) as number, 'cable run')}{' '}
                          already drawn on your canvases. Renaming it instead keeps them.
                        </span>
                      )}
                      {!isCatalogue && (
                        <button
                          type="button"
                          onClick={() => dropPort(port.key)}
                          className={cx(btn.sm, btn.danger)}
                        >
                          Remove anyway
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={() => setPendingRemoval(null)}
                        className={cx(btn.sm, btn.secondary)}
                      >
                        Keep it
                      </button>
                    </div>
                  )}
                </li>
              ))}
            </ul>
          )}

          {SIGNAL_CATEGORIES.map((signal) => (
            <datalist key={signal} id={`connectors-${signal}`}>
              {CONNECTORS_BY_SIGNAL[signal].map((connector) => (
                <option key={connector} value={connector} />
              ))}
            </datalist>
          ))}
        </section>
      </div>

      <div className="flex shrink-0 items-center gap-2 border-t border-[var(--border)] bg-surface/80 p-4 backdrop-blur">
        <button type="submit" disabled={saving} className={cx(btn.base, btn.primary)}>
          {saving ? 'Saving…' : device ? 'Save changes' : 'Create device'}
        </button>
        <button type="button" onClick={onCancel} className={cx(btn.base, btn.ghost)}>
          Cancel
        </button>
        {error && <p role="alert" className="text-xs text-danger">{error}</p>}
      </div>
    </form>
  )
}
